<template>
  <div class="processing-container">
    <div class="processing-header">
      <Heading :level="1">Processing Your Manuscript</Heading>
      <p class="patience-message">
        We are analyzing your manuscript across multiple frameworks. This process may take up to 30 minutes.
      </p>
      <div class="overall-progress">
        <div class="progress-bar">
          <div class="progress-fill" :style="{ width: getOverallProgress() + '%' }"></div>
        </div>
        <span class="progress-text">{{ getOverallProgress() }}% Complete</span>
      </div>
    </div>

    <div v-if="availableOutputs.length > 0" class="available-outputs">
      <Heading :level="3">Available Documents</Heading>
      <div class="outputs-grid">
        <router-link
          v-for="output in availableOutputs"
          :key="`${output.frameworkId}:${output.outputName}`"
          :to="getOutputUrl(output.frameworkId, output.outputName)"
          class="output-card-link"
        >
          <Button variant="raw" class="output-card">
            <div class="output-info">
              <div class="output-name">
                {{ output.outputName.replace(/_/g, ' ').replace(/^\w/, (c: string) => c.toUpperCase()) }}
                <span v-if="output.isStreaming" class="output-icon spinning">
                  <QIcon :path="mdiLoading" :size="20" color="var(--color-accent)" />
                </span>
              </div>
              <div class="output-framework">{{ output.frameworkName }}</div>
            </div>
          </Button>
        </router-link>
      </div>
    </div>

    <!-- Error Display Section -->
    <div v-if="frameworkErrors.length > 0" class="errors-section">
      <div class="errors-header">
        <QIcon :path="mdiAlertCircle" :size="20" color="var(--color-danger)" />
        <Heading :level="3">Processing Issues</Heading>
      </div>
      <div class="errors-list">
        <div v-for="error in frameworkErrors" :key="error.frameworkId" class="error-item">
          <div class="error-framework">{{ error.frameworkName }}</div>
          <div class="error-message">{{ error.error }}</div>
        </div>
      </div>
    </div>

    <!-- Failed Steps -->
    <div v-if="projectStore.stepErrors.length > 0" class="errors-section failed-steps-section">
      <div class="errors-header">
        <QIcon :path="mdiAlertCircle" :size="20" color="var(--color-danger)" />
        <Heading :level="3">Failed Steps</Heading>
      </div>
      <div class="errors-list">
        <div
          v-for="stepError in projectStore.stepErrors"
          :key="`${stepError.frameworkId}-${stepError.step}`"
          class="error-item failed-step-item"
        >
          <div class="failed-step-header">
            <div class="error-framework">
              {{ getFrameworkName(stepError.frameworkId) }} - Step {{ stepError.step }}
              <span v-if="stepError.name" class="step-name">: {{ stepError.name }}</span>
            </div>
            <Button v-if="debugMode" variant="primary" size="small" @click="handleRetryStep(stepError)">Retry</Button>
          </div>
          <div class="error-message">{{ stepError.error }}</div>
        </div>
      </div>
    </div>

    <div class="sections-container">
      <template v-if="projectStore.frameworks && projectStore.frameworks.length > 0">
        <div v-for="framework in projectStore.frameworks" :key="framework.frameworkId" class="framework-section">
          <div class="framework-header">
            <div class="header-left">
              <div
                class="framework-icon"
                :class="{
                  spinning: framework.status !== 'complete' && framework.status !== 'error',
                }"
              >
                <QIcon
                  :path="getStatusIcon(framework.status || 'complete')"
                  :size="20"
                  :color="getStatusColor(framework.status || 'complete')"
                />
              </div>
              <Heading :level="3">{{ framework.name }}</Heading>
            </div>
            <div class="header-right">
              <span v-if="framework.status" class="step-counter"
                >{{ framework.completedSteps }}/{{ framework.totalSteps }}</span
              >
            </div>
          </div>

          <div class="framework-progress">
            <div class="progress-bar">
              <div
                class="progress-fill"
                :style="{
                  width: framework.status
                    ? (framework.totalSteps > 0 ? (framework.completedSteps / framework.totalSteps) * 100 : 0) + '%'
                    : '100%',
                }"
              ></div>
            </div>
          </div>

          <div class="framework-content">
            <div v-if="framework.status === 'complete'" class="framework-complete">
              <p>All steps completed successfully!</p>
            </div>
            <template v-else-if="framework.currentSteps && framework.currentSteps.length > 0">
              <div class="current-steps">
                <Button
                  variant="raw"
                  :iconLeft="expandedSteps.has(framework.frameworkId) ? 'chevron-down' : 'chevron-right'"
                  class="steps-toggle"
                  @click="toggleStepList(framework.frameworkId)"
                >
                  {{
                    framework.currentSteps && framework.currentSteps.length > 0
                      ? `Currently working on ${framework.currentSteps.length} task${framework.currentSteps.length > 1 ? 's' : ''}`
                      : 'Preparing...'
                  }}
                </Button>
                <div v-if="expandedSteps.has(framework.frameworkId)" class="steps-list">
                  <div v-for="step in framework.currentSteps || []" :key="step.step" class="step-item">
                    <strong>Step {{ step.step }}: {{ step.name }}</strong>
                    <p v-if="step.description">{{ step.description }}</p>
                  </div>
                </div>
              </div>
            </template>
            <template v-else>
              <div class="current-steps">
                <Button
                  variant="raw"
                  :iconLeft="expandedSteps.has(framework.frameworkId) ? 'chevron-down' : 'chevron-right'"
                  class="steps-toggle"
                  @click="toggleStepList(framework.frameworkId)"
                >
                  Preparing...
                </Button>
              </div>
            </template>
          </div>
        </div>
      </template>
      <div v-else class="loading-section">
        <div class="spinning">
          <QIcon :path="mdiLoading" :size="32" color="var(--color-accent)" />
        </div>
        <p>Initializing your edit...</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Button, Heading } from '@dabble/quillui';
import { mdiAlertCircle, mdiCheckCircle, mdiLoading } from '@mdi/js';
import { marked } from 'marked';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { buildOutputUrl } from '../lib/urlUtils';
import { useProjectStore } from '../stores/project';
import QIcon from './QIcon.vue';

const route = useRoute();
const projectId = computed(() => route.params['projectId']);
const projectStore = useProjectStore();

// Props
defineProps<{
  debugMode?: boolean;
}>();

// Configure marked options
marked.setOptions({
  gfm: true,
  breaks: true,
});

// Track which frameworks have expanded step lists
const expandedSteps = ref<Set<string>>(new Set());

// Generate nested URL for output
function getOutputUrl(frameworkId: string, outputName: string): string {
  return buildOutputUrl(projectId.value as string, frameworkId, outputName);
}

function toggleStepList(frameworkId: string) {
  const newExpanded = new Set(expandedSteps.value);
  if (newExpanded.has(frameworkId)) {
    newExpanded.delete(frameworkId);
  } else {
    newExpanded.add(frameworkId);
  }
  expandedSteps.value = newExpanded;
}

function getStatusIcon(status: string) {
  switch (status) {
    case 'complete':
      return mdiCheckCircle;
    case 'error':
      return mdiAlertCircle;
    default:
      return mdiLoading;
  }
}

function getStatusColor(status: string) {
  switch (status) {
    case 'complete':
      return 'var(--color-success)';
    case 'error':
      return 'var(--color-danger)';
    default:
      return 'var(--color-accent)';
  }
}

function getOverallProgress() {
  if (!projectStore.frameworks || projectStore.frameworks.length === 0) return 0;

  // Calculate total and completed files
  let totalSteps = 0;
  let completedSteps = 0;

  for (const framework of projectStore.frameworks) {
    totalSteps += framework.totalSteps;
    completedSteps += framework.completedSteps;
  }

  return totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;
}

const availableOutputs = computed(() => {
  const outputs: Array<{ frameworkId: string; frameworkName: string; outputName: string; isStreaming: boolean }> = [];

  // Build available outputs from frameworks
  for (const framework of projectStore.frameworks) {
    for (const output of framework.outputs) {
      if (output.status === 'started' || output.status === 'complete') {
        outputs.push({
          frameworkId: framework.frameworkId,
          frameworkName: framework.name,
          outputName: output.name,
          isStreaming: output.status === 'started',
        });
      }
    }
  }

  return outputs;
});

const frameworkErrors = computed(() => {
  const errors: Array<{ frameworkId: string; frameworkName: string; error: string }> = [];

  for (const framework of projectStore.frameworks) {
    if (framework.error) {
      errors.push({
        frameworkId: framework.frameworkId,
        frameworkName: framework.name,
        error: framework.error,
      });
    }
  }

  return errors;
});

function getFrameworkName(frameworkId: string): string {
  const framework = projectStore.frameworks.find(f => f.frameworkId === frameworkId);
  return framework?.name || frameworkId;
}

async function handleRetryStep(stepError: any) {
  if (!confirm(`Retry step ${stepError.step}? This will attempt to re-run the failed step.`)) {
    return;
  }

  try {
    const response = await fetch(`/api/projects/${projectId.value}/retry-step`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        frameworkId: stepError.frameworkId,
        step: stepError.step,
      }),
    });

    if (!response.ok) {
      throw new Error('Failed to retry step');
    }

    // Reload to refresh status
    location.reload();
  } catch (error) {
    console.error('Error retrying step:', error);
    alert('Failed to retry step. Please try again.');
  }
}

// Emit file click event - removed since unused
// const emit = defineEmits<{
//   'file-click': [frameworkId: string, fileName: string];
// }>();
</script>

<style scoped>
.processing-container {
  max-width: 900px;
  margin: 0 auto;
  padding: var(--spacing-xl);
  height: 100vh;
  background: linear-gradient(180deg, var(--color-bg-primary) 0%, var(--color-bg-secondary) 100%);
}

.processing-header {
  text-align: center;
  margin-bottom: var(--spacing-xl);
  padding-bottom: var(--spacing-xl);
}

.processing-header h1 {
  margin-bottom: var(--spacing-xs);
  color: var(--color-text-primary);
}

.author {
  font-size: 18px;
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-md);
}

.patience-message {
  margin-bottom: var(--spacing-lg);
  color: var(--color-text-secondary);
  font-size: 14px;
  line-height: 1.4;
}

.overall-progress {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);
  max-width: 500px;
  margin: 0 auto;
}

.progress-bar {
  height: 8px;
  background: var(--color-bg-tertiary);
  border-radius: 4px;
  overflow: hidden;
  flex: 1;
}

.progress-fill {
  height: 100%;
  background: var(--color-accent);
  transition: width 0.3s ease;
}

.progress-text {
  font-size: 13px;
  color: var(--color-text-secondary);
  font-weight: 500;
  min-width: 90px;
  text-align: right;
}

.sections-container {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xl);
}

.loading-section,
.error-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-md);
  padding: var(--spacing-xl);
  text-align: center;
}

.spinning :deep(svg) {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.framework-section {
  padding-bottom: var(--spacing-lg);
  border-bottom: 1px solid var(--color-border-primary);
}

.framework-section:last-child {
  border-bottom: none;
}

.framework-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-sm);
}

.header-left,
.header-right {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.framework-icon {
  display: flex;
  align-items: center;
  justify-content: center;
}

.framework-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  color: var(--color-text-primary);
}

.step-counter {
  font-size: 13px;
  color: var(--color-text-secondary);
  font-weight: 500;
}

.framework-progress {
  margin-bottom: var(--spacing-sm);
}

.framework-progress .progress-bar {
  height: 6px;
}

.framework-content {
  margin-top: var(--spacing-md);
}

.pending-review {
  padding: var(--spacing-sm);
  font-size: 13px;
  color: var(--color-text-secondary);
  font-style: italic;
}

.framework-error {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  font-size: 13px;
  color: var(--color-danger);
  padding: var(--spacing-sm);
  background: rgba(239, 68, 68, 0.1);
  border-radius: var(--border-radius);
  margin-top: var(--spacing-sm);
}

.current-steps {
  margin-bottom: var(--spacing-md);
}

.steps-toggle {
  display: flex;
  align-items: center;
  gap: var(--spacing-xs);
  background: none !important;
  border: none !important;
  padding: var(--spacing-sm);
  cursor: pointer;
  font-size: 13px;
  color: var(--color-text-secondary);
  width: 100%;
  text-align: left;
  transition: background var(--transition-base);
  box-shadow: none !important;
  transform: none !important;
}
.steps-toggle span {
  display: flex;
  align-items: center;
}

.steps-toggle:hover {
  background: var(--color-bg-tertiary) !important;
}

.steps-list {
  margin-left: var(--spacing-lg);
  padding: var(--spacing-sm) 0;
  border-left: 2px solid var(--color-border-primary);
}

.step-item {
  padding: var(--spacing-xs) var(--spacing-md);
  margin-bottom: var(--spacing-xs);
}

.step-item strong {
  font-size: 12px;
  color: var(--color-text-primary);
}

.step-item p {
  font-size: 11px;
  color: var(--color-text-secondary);
  margin: 2px 0 0 0;
}

.framework-complete {
  padding: var(--spacing-sm);
  font-size: 13px;
  color: var(--color-success);
  font-weight: 500;
}

.available-outputs {
  margin-bottom: var(--spacing-xl);
  padding: var(--spacing-lg);
  background: var(--color-bg-secondary);
  border-radius: var(--border-radius);
}

.available-outputs h3 {
  margin: 0 0 var(--spacing-md) 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-primary);
}

.outputs-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: var(--spacing-md);
}

.output-card-link {
  text-decoration: none;
  display: block;
}

.output-card {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-md);
  background: white !important;
  border: 1px solid var(--color-border-primary) !important;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all var(--transition-base);
  text-align: left;
  width: 100%;
  box-shadow: var(--shadow-sm) !important;
  transform: none !important;
}

.output-card:hover {
  border-color: var(--color-accent) !important;
  transform: translateY(-2px) !important;
  box-shadow: var(--shadow-md) !important;
}

span.output-icon {
  display: inline-block;
}

.output-icon {
  flex-shrink: 0;
}

.output-info {
  flex: 1;
  min-width: 0;
}

.output-name {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.output-framework {
  font-size: 12px;
  color: var(--color-text-secondary);
  margin-top: 2px;
}

.errors-section {
  margin-bottom: var(--spacing-xl);
  padding: var(--spacing-lg);
  background: rgba(239, 68, 68, 0.05);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: var(--border-radius);
}

.debug-errors {
  background: rgba(255, 165, 0, 0.05);
  border-color: rgba(255, 165, 0, 0.3);
}

.failed-steps-section {
  background: rgba(239, 68, 68, 0.08);
}

.failed-step-item {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.failed-step-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--spacing-sm);
}

.step-name {
  font-weight: normal;
  color: var(--color-text-secondary);
}

.errors-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-md);
}

.errors-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--color-danger);
}

.errors-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.error-item {
  padding: var(--spacing-sm);
  background: white;
  border-radius: var(--border-radius);
  border-left: 3px solid var(--color-danger);
}

.error-framework {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin-bottom: 4px;
}

.error-message {
  font-size: 13px;
  color: var(--color-text-secondary);
  line-height: 1.4;
}
</style>
